import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { Loader2, Trash2, Upload } from 'lucide-react';
import { db, storage } from '@/lib/firebase';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toaster';
import { SettingsSchema, type Settings } from '@/types/schemas';

const DEFAULT_SETTINGS: Settings = {
  businessName: 'CHHABIGHAR',
  branding: { logoUrl: '', themeColor: '#C9A441', tagline: 'ছবি ঘর' },
  gstNumber: '',
  invoicePrefix: 'INV-',
  quotationPrefix: 'QT-',
  taxRate: 0,
  address: '',
  phone: '',
  email: '',
};

export function SettingsPage() {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [removingLogo, setRemovingLogo] = useState(false);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: async (): Promise<Settings> => {
      const snap = await getDoc(doc(db, 'settings', 'global'));
      if (snap.exists()) return SettingsSchema.parse(snap.data());
      return DEFAULT_SETTINGS;
    },
  });

  const form = useForm({
    resolver: zodResolver(SettingsSchema),
    values: settings ?? DEFAULT_SETTINGS,
  });

  useEffect(() => {
    if (settings) form.reset(settings);
  }, [settings, form]);

  const save = useMutation({
    mutationFn: async (values: Settings) => {
      await setDoc(
        doc(db, 'settings', 'global'),
        { ...values, updatedAt: serverTimestamp() },
        { merge: true },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings'] });
      toast({ title: 'Settings saved', variant: 'success' });
    },
    onError: (e) => toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  async function uploadLogo(file: File) {
    setUploadingLogo(true);
    try {
      const path = `branding/logo_${Date.now()}_${file.name}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file, { contentType: file.type });
      const url = await getDownloadURL(r);
      form.setValue('branding.logoUrl', url, { shouldDirty: true });
      toast({ title: 'Logo uploaded', variant: 'success' });
    } catch (e) {
      toast({ title: 'Upload failed', description: String(e), variant: 'destructive' });
    } finally {
      setUploadingLogo(false);
    }
  }

  async function removeLogo() {
    const url = form.getValues('branding.logoUrl');
    if (!url) return;
    setRemovingLogo(true);
    try {
      try {
        await deleteObject(storageRef(storage, url));
      } catch {
        // Still clear the saved URL if delete fails (e.g. legacy URL).
      }
      form.setValue('branding.logoUrl', '', { shouldDirty: true });
      toast({ title: 'Logo removed', variant: 'success' });
    } catch (e) {
      toast({ title: 'Could not remove logo', description: String(e), variant: 'destructive' });
    } finally {
      setRemovingLogo(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Business information, branding, GST, and document numbering."
      />

      <form
        onSubmit={form.handleSubmit((v) => save.mutate(v))}
        className="grid gap-4 grid-cols-1 lg:grid-cols-2"
      >
        <Card>
          <CardHeader>
            <CardTitle>Business</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Field label="Business name">
              <Input {...form.register('businessName')} />
            </Field>
            <Field label="Tagline">
              <Input {...form.register('branding.tagline')} />
            </Field>
            <Field label="GST number">
              <Input {...form.register('gstNumber')} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Invoice prefix">
                <Input {...form.register('invoicePrefix')} />
              </Field>
              <Field label="Quotation prefix">
                <Input {...form.register('quotationPrefix')} />
              </Field>
            </div>
            <Field label="Default tax %">
              <Input type="number" min="0" step="0.01" {...form.register('taxRate')} />
            </Field>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Branding & contact</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label className="text-xs">Logo</Label>
              <div className="mt-1 flex flex-wrap items-center gap-3">
                <div className="h-16 w-16 overflow-hidden rounded border bg-muted">
                  {form.watch('branding.logoUrl') ? (
                    <img
                      src={form.watch('branding.logoUrl')}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center text-xs text-muted-foreground">
                      No logo
                    </div>
                  )}
                </div>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadLogo(f);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingLogo || removingLogo}
                >
                  {uploadingLogo ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Upload
                </Button>
                {form.watch('branding.logoUrl') ? (
                  <Button
                    type="button"
                    variant="outline"
                    className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                    onClick={() => void removeLogo()}
                    disabled={uploadingLogo || removingLogo}
                  >
                    {removingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                    Remove
                  </Button>
                ) : null}
              </div>
            </div>
            <Field label="Theme color">
              <Input {...form.register('branding.themeColor')} />
            </Field>
            <Field label="Address">
              <Textarea rows={2} {...form.register('address')} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Phone">
                <Input {...form.register('phone')} />
              </Field>
              <Field label="Email">
                <Input type="email" {...form.register('email')} />
              </Field>
            </div>
          </CardContent>
        </Card>

        <div className="lg:col-span-2 flex justify-end">
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
